import React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector } from 'redux-form'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// components
import { ReduxEntitySelect, ReduxSwitch } from 'components/FormElements'
// themes
import { colors } from 'themes/index'
// icons
import GridOnRounded from '@mui/icons-material/GridOnRounded'
// constants
import { Forms } from 'constants/forms'
import { Modals } from 'constants/modals'
// views
import { Button, ErrorMessage, SwitchGroup, Wrapper } from './views'
// graphql
import { RISK_VIEW_CREATE_MUTATION } from 'graphql/risk/mutations'
import { RISK_TEMPLATE_SELECT_QUERY } from 'graphql/risk/queries'
// types
import { FieldType, IField, IRiskTemplate, IRiskViewResponse } from 'types/graphql'
// utils
import { safeGetOr } from 'utils/fp'
import { requiredBoolean } from 'utils/validators'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

const formSelector = formValueSelector(Forms.RISK_TEMPLATE_FORM_ADD_FORM)

type QueryResponse = {
  riskViewCreate: IRiskViewResponse
}

interface Props {
  error?: string
}

const LinkingSettings: React.FC<Props> = ({ error }) => {
  const dispatch = useDispatch()

  const [riskViewCreate] = useMutation<QueryResponse>(RISK_VIEW_CREATE_MUTATION)

  const { showModal } = useShowModal({ modalType: Modals.LINKED_FORM_VIEW_MODAL })

  const canLink = useSelector((state) => formSelector(state, 'canLink'))
  const template = useSelector((state) => formSelector(state, 'sourceTemplate'))
  const riskViewId = useSelector((state) => formSelector(state, 'riskViewId'))

  const { createNewRecords, referenceRecords, copyRecords } =
    useSelector((state) => formSelector(state, 'linkingSettings')) || {}

  const canUpdateView = template && canLink

  const onUpdateSearchView = async () => {
    try {
      let id = riskViewId

      if (!riskViewId) {
        const {
          data: {
            riskViewCreate: { data: riskView }
          }
        } = await riskViewCreate({
          variables: {
            input: {
              name: template.label,
              riskTemplateId: template.value,
              settings: { fields: [] },
              isLinkedView: true
            }
          }
        })

        id = riskView?.id

        dispatch(change(Forms.RISK_TEMPLATE_FORM_ADD_FORM, 'riskViewId', id))
      }

      showModal({ riskViewId: id })
    } catch (e) {
      console.log('[FormSettings.onUpdateSearchView]: ', parseGraphQLErrorMessages(e))
    }
  }

  const onCanLinkChange = () => {
    if (copyRecords) {
      dispatch(change(Forms.RISK_TEMPLATE_FORM_ADD_FORM, 'linkingSettings.copyRecords', false))
    }

    if (referenceRecords) {
      dispatch(change(Forms.RISK_TEMPLATE_FORM_ADD_FORM, 'linkingSettings.referenceRecords', false))
    }
  }

  const getFieldsOptions = (data) => {
    const disabledFieldTypes = [FieldType.Table, FieldType.Label, FieldType.Section]

    const fields = safeGetOr<IRiskTemplate, any, IField[]>([], 'data.riskTemplates.data[0].fields')(data)

    return fields
      .filter((field: IField) => field.active && !disabledFieldTypes.includes(field.fieldType))
      .map(({ id, label }) => ({
        value: id,
        label
      }))
  }

  return (
    <Wrapper>
      <ReduxSwitch
        reverse
        name='linkingSettings.createNewRecords'
        label='Create new record'
        validate={!canLink && requiredBoolean}
      />
      <ReduxSwitch
        reverse
        name='canLink'
        label='Link to existing record'
        listenOnChange={onCanLinkChange}
        validate={!createNewRecords && requiredBoolean}
      />
      <SwitchGroup>
        <ReduxSwitch
          reverse
          name='linkingSettings.referenceRecords'
          label='Search and reference'
          disabled={!canLink}
          validate={canLink && !copyRecords && requiredBoolean}
        />
        <ReduxSwitch
          reverse
          name='linkingSettings.copyRecords'
          label='Search and copy'
          disabled={!canLink}
          validate={canLink && !referenceRecords && requiredBoolean}
        />
      </SwitchGroup>
      <Button onClick={onUpdateSearchView} disabled={!canUpdateView}>
        <GridOnRounded htmlColor={canUpdateView ? colors.blue500 : colors.gray300} />
        Update Search View
      </Button>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <ReduxSwitch reverse name='linkingSettings.mirrorParentPermissions' label='Mirror parent permissions' />
      <ReduxEntitySelect
        isMultiple
        skip={!template}
        menuPlacement='top'
        menuPortalTarget={null}
        getOptions={getFieldsOptions}
        label='Exclude fields on copying'
        query={RISK_TEMPLATE_SELECT_QUERY}
        name='linkingSettings.excludeFieldsWhenCopying'
        variables={{ filter: { ids: [template?.value] } }}
      />
    </Wrapper>
  )
}

export default LinkingSettings
